import { useState, useRef, useEffect } from 'react';
import { Home } from 'lucide-react';
import {
  formatAbsenceDuration,
  startTemporaryAbsence,
  endTemporaryAbsence,
  type ContextType,
} from '../lib/temporaryAbsencesApi';

export interface ActiveAbsenceInfo {
  id: number;
  started_at: string;
}

interface AbsenceToggleProps {
  contextType: ContextType;
  contextId: number;
  studentUnid: string;
  activeAbsence: ActiveAbsenceInfo | null;
  onStarted: () => void;
  onEnded: () => void;
  /** compact = single icon button; false = button with label */
  compact?: boolean;
  className?: string;
}

export default function AbsenceToggle({
  contextType,
  contextId,
  studentUnid,
  activeAbsence,
  onStarted,
  onEnded,
  compact = true,
  className = '',
}: AbsenceToggleProps) {
  const [loading, setLoading] = useState(false);
  const [localActiveAbsence, setLocalActiveAbsence] = useState<ActiveAbsenceInfo | null>(activeAbsence);
  const [, setTicker] = useState(0);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setLocalActiveAbsence(activeAbsence);
  }, [activeAbsence]);

  useEffect(() => {
    if (!localActiveAbsence) {
      return;
    }
    const id = window.setInterval(() => setTicker((value) => value + 1), 1000);
    return () => window.clearInterval(id);
  }, [localActiveAbsence]);

  const handleStart = async () => {
    setLoading(true);
    try {
      const result = await startTemporaryAbsence(contextType, contextId, studentUnid);
      setLocalActiveAbsence({
        id: result.data.id,
        started_at: result.data.started_at,
      });
      onStarted();
    } catch (err: any) {
      console.error('Start absence failed:', err);
      const msg = err?.response?.data?.error || 'Abwesenheit konnte nicht gestartet werden';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleEnd = async () => {
    setLoading(true);
    try {
      await endTemporaryAbsence(contextType, contextId, studentUnid);
      setLocalActiveAbsence(null);
      onEnded();
    } catch (err: any) {
      console.error('End absence failed:', err);
      const msg = err?.response?.data?.error || 'Abwesenheit konnte nicht beendet werden';
      alert(msg);
    } finally {
      setLoading(false);
    }
  };

  const isAway = !!localActiveAbsence;
  const awayDurationText = localActiveAbsence ? formatAbsenceDuration(localActiveAbsence.started_at) : '';
  const title = isAway
    ? `Weg – seit ${awayDurationText}. Klicken = wieder da.`
    : 'Anwesend. Klicken = weg melden.';

  const handleButtonClick = () => {
    if (isAway) {
      handleEnd();
    } else {
      handleStart();
    }
  };

  return (
    <div className={`group relative inline-block ${className}`} ref={dropdownRef}>
      <button
        type="button"
        onClick={handleButtonClick}
        disabled={loading}
        title={title}
        className={`flex items-center justify-center rounded transition-colors ${
          isAway
            ? 'bg-amber-500 text-white hover:bg-amber-600 p-2'
            : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 p-2'
        } ${compact ? 'p-2' : 'px-2 py-1.5 text-sm'}`}
      >
        <Home className="w-4 h-4" />
      </button>

      {isAway && (
        <div className="pointer-events-none absolute left-1/2 top-full z-50 mt-1 -translate-x-1/2 whitespace-nowrap rounded-md bg-gray-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition-opacity group-hover:opacity-100">
          Abgängig seit {awayDurationText}
        </div>
      )}
    </div>
  );
}
